"use client";

import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import { CheckCircle2, MessageSquare, Clock, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "./Button";
import { CopyButton } from "./CopyButton";

interface FormSuccessProps {
  referenceId?: string;
  title?: string;
  message?: string;
  slaTime?: string;
  onReset?: () => void;
  className?: string;
}

export function FormSuccess({
  referenceId = `JS-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
  title = "Inquiry Dispatched to Engineering Leads",
  message = "Thank you! Your project requirements have been received and logged directly into our engineering pipeline.",
  slaTime = "Under 15 minutes",
  onReset,
  className = "",
}: FormSuccessProps) {
  useEffect(() => {
    try {
      confetti({
        particleCount: 90,
        spread: 70,
        origin: { y: 0.6 },
      });
    } catch {
      // Ignore if canvas-confetti is unavailable
    }
  }, []);

  return (
    <div
      className={`rounded-2xl border border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-950/20 p-8 text-center space-y-5 animate-in fade-in zoom-in-95 duration-300 ${className}`}
    >
      {/* Animated Checkmark Badge */}
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center border border-emerald-500/30 shadow-lg shadow-emerald-500/10">
        <CheckCircle2 className="w-8 h-8 animate-in zoom-in-50 duration-300" />
      </div>

      <div className="space-y-1.5 max-w-md mx-auto">
        <h3 className="text-xl font-heading font-extrabold text-slate-900 dark:text-slate-100">
          {title}
        </h3>
        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          {message}
        </p>
      </div>

      {/* Reference Card */}
      <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm text-xs">
        <span className="text-slate-400">Reference ID:</span>
        <code className="font-mono font-bold text-slate-900 dark:text-slate-100">
          {referenceId}
        </code>
        <CopyButton text={referenceId} variant="icon" showToast={false} />
      </div>

      {/* SLA Guarantee Box */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 text-xs text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-emerald-500" />
          <span>Guaranteed response: <strong>{slaTime}</strong></span>
        </div>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-700">•</span>
        <div className="flex items-center gap-1.5">
          <MessageSquare className="w-4 h-4 text-blue-500" />
          <span>WhatsApp notification sent</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pt-3 flex flex-wrap items-center justify-center gap-3">
        <a
          href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I%20just%20submitted%20an%20inquiry%20and%20would%20like%20to%20chat."
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button variant="gradient" size="sm" className="flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Chat Live on WhatsApp</span>
            <ArrowRight className="w-3 h-3" />
          </Button>
        </a>

        {onReset && (
          <Button
            variant="outline"
            size="sm"
            onClick={onReset}
            className="flex items-center gap-1.5 text-xs"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Submit Another Project</span>
          </Button>
        )}
      </div>
    </div>
  );
}
